import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Observable, from, throwError, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface PushTokenResponse {
  id: string;
  token: string;
  platform: 'android' | 'ios';
}

@Injectable({ providedIn: 'root' })
export class PushRegistrationService {
  private readonly http = inject(HttpClient);

  /**
   * Registers the device for push notifications:
   * 1. Checks that the platform is hybrid (Capacitor WebView)
   * 2. Requests notification permissions
   * 3. Registers with FCM via Capacitor
   * 4. Sends the token to the backend
   *
   * Returns the backend response or throws a user-friendly error.
   */
  register(): Observable<PushTokenResponse | null> {
    if (!Capacitor.isNativePlatform()) {
      return of(null);
    }

    return from(this.requestPermissionAndRegister()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No se pudo obtener el token de notificaciones.'));
        }

        const detectedPlatform: 'android' | 'ios' = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

        return this.http.post<PushTokenResponse>(`${environment.apiUrl}/push/tokens`, {
          token,
          platform: detectedPlatform,
        });
      }),
      catchError((error) => {
        // Permission denied by user
        if (error?.message?.includes('permission') || error?.message?.includes('denied')) {
          return throwError(() => ({
            friendlyMessage:
              'Permiso de notificaciones denegado. Podés activarlo desde la configuración del dispositivo.',
          }));
        }
        // FCM registration failure
        if (error?.message?.includes('registration')) {
          return throwError(() => ({
            friendlyMessage: 'Error al registrar el dispositivo para notificaciones.',
          }));
        }
        // API / network error — preserve existing friendlyMessage if present
        const message =
          error?.friendlyMessage ||
          error?.error?.message ||
          'Error al registrar el dispositivo. Intenta de nuevo.';
        return throwError(() => ({ ...error, friendlyMessage: message }));
      }),
    );
  }

  /**
   * Unregisters a device token from the backend.
   * @param tokenId — The backend ID of the push token to remove.
   */
  unregister(tokenId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/push/tokens/${tokenId}`).pipe(
      catchError((error) => {
        const message =
          error?.friendlyMessage ||
          error?.error?.message ||
          'Error al desregistrar el dispositivo.';
        return throwError(() => ({ ...error, friendlyMessage: message }));
      }),
    );
  }

  /**
   * Requests permission and registers with FCM via Capacitor.
   * In Capacitor 8, PushNotifications.register() returns void;
   * the FCM/APNS token is delivered via the 'registration' event.
   * Returns the FCM token string, or null if permission was denied.
   */
  private async requestPermissionAndRegister(): Promise<string | null> {
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive === 'denied') {
      return null;
    }

    // Wrap the event-based registration in a promise
    return new Promise<string | null>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Push registration timeout'));
      }, 30000);

      PushNotifications.addListener('registration', (token) => {
        clearTimeout(timeout);
        resolve(token.value);
      }).catch((err: unknown) => {
        clearTimeout(timeout);
        reject(err);
      });

      PushNotifications.addListener('registrationError', (error) => {
        clearTimeout(timeout);
        reject(new Error(error.error));
      }).catch((err: unknown) => {
        clearTimeout(timeout);
        reject(err);
      });

      PushNotifications.register().catch((err: unknown) => {
        clearTimeout(timeout);
        reject(err);
      });
    });
  }
}
